import React from 'react';

class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      errorType: null,
      retryCount: 0,
      errorDetails: null
    };
  }

  static getDerivedStateFromError(error) {
    // ダッシュボード固有のエラーを分類
    let errorType = 'UNKNOWN_ERROR';
    if (error.message.includes('path') || error.message.includes('SVG')) {
      errorType = 'SVG_ERROR';
    } else if (error.message.includes('user') || error.message.includes('profile')) {
      errorType = 'USER_PROFILE_ERROR';
    } else if (error.message.includes('chart') || error.message.includes('Chart')) {
      errorType = 'CHART_ERROR';
    } else if (error.message.includes('team') || error.message.includes('Team')) {
      errorType = 'TEAM_ERROR';
    }
    
    return { 
      hasError: true, 
      errorType,
      errorDetails: error.message
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 Dashboard error caught:', {
      error,
      errorInfo,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });

    // 開発環境でのみ詳細ログを出力
    if (process.env.NODE_ENV === 'development') {
      console.groupCollapsed('🔍 Error Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }
  }

  handleRetry = () => {
    console.log('🔄 Retrying dashboard render...');
    this.setState(prevState => ({
      hasError: false,
      errorType: null,
      errorDetails: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  getErrorMessage = () => {
    switch (this.state.errorType) {
      case 'SVG_ERROR':
        return 'チャートやアイコンの描画中にエラーが発生しました。';
      case 'USER_PROFILE_ERROR':
        return 'ユーザープロファイルの読み込み中にエラーが発生しました。';
      case 'CHART_ERROR':
        return 'データ可視化コンポーネントでエラーが発生しました。';
      case 'TEAM_ERROR':
        return 'チーム機能でエラーが発生しました。';
      default:
        return 'ダッシュボードの表示中に予期しないエラーが発生しました。';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="p-8 bg-red-50 text-center text-red-700 rounded-lg border border-red-200 max-w-md mx-auto shadow-lg">
            <div className="mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-xl font-bold text-red-800">
                ダッシュボードエラー
              </h3>
            </div>
            
            <p className="text-red-700 mb-2">
              {this.getErrorMessage()}
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.errorDetails && (
              <details className="text-left text-xs text-red-600 mt-4 p-2 bg-red-100 rounded">
                <summary className="cursor-pointer font-semibold">技術詳細</summary>
                <pre className="mt-2 whitespace-pre-wrap">{this.state.errorDetails}</pre>
              </details>
            )}
            
            <div className="flex flex-col gap-2 mt-6">
              {this.state.retryCount < 3 && (
                <button 
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  再試行 ({3 - this.state.retryCount}回まで)
                </button>
              )}
              
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                ページを更新
              </button>
              
              <button 
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                前のページに戻る
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return this.props.children;
  }
}

export default DashboardErrorBoundary;